import React from 'react';
import ActionCard, { ActionCardVariant } from 'components/Common/Cards/ActionCard/ActionCard';
import {
  CREDIT_SCORE_CATEGORIES_LIST,
  getCreditScoreCategory,
  getCreditScoreCategoryProgressPercentage,
  MIN_CREDIT_SCORE,
} from 'enums/CreditScoreCategory';
import clsx from 'clsx';

import styles from './CreditScoreGauge.module.scss';

export enum CheckFrequency {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

interface CreditScoreGaugeProps {
  score?: number;
  change?: number;
  className?: string;
  checkFrequency?: CheckFrequency;
}

const dataColors = ['red', 'orange', 'yellow', 'green', 'turquoise'];

export const getScorePosition = (score: number): number => {
  const clampedScore = Math.max(MIN_CREDIT_SCORE, score);
  const categoryScore = getCreditScoreCategory(clampedScore);
  const categoryPercentageProgress = getCreditScoreCategoryProgressPercentage(clampedScore);
  const categoryIndex = CREDIT_SCORE_CATEGORIES_LIST.indexOf(categoryScore);
  return (
    (100 / CREDIT_SCORE_CATEGORIES_LIST.length) * categoryIndex +
    categoryPercentageProgress / CREDIT_SCORE_CATEGORIES_LIST.length
  );
};

export const getChangeText = (change?: number): string | null => {
  if (!change) return null;
  const sign = change > 0 ? '+' : '';
  return `${sign}${change} ${Math.abs(change) === 1 ? 'point' : 'points'}`;
};

const CreditScoreGauge: React.FC<CreditScoreGaugeProps> = ({ score, change, className, checkFrequency }) => {
  if (!score) return null;
  const position = getScorePosition(score);
  const changeText = getChangeText(change);
  const changeClass = change && change > 0 ? styles.positive : undefined;
  return (
    <ActionCard variant={ActionCardVariant.White} className={className}>
      <div className={styles.progress}>
        <div className={styles.textRow}>
          <h3 className={styles.score}>{score}</h3>
          {checkFrequency && <span className={styles.checkedDaily}>Checked {checkFrequency}</span>}
          {changeText && <span className={clsx(styles.changeIndicator, changeClass)}>{changeText}</span>}
        </div>
        <div className={styles.pillsContainer}>
          <div className={styles.pillsRow}>
            {dataColors.map((color) => (
              <div className={styles.pill} data-color={color} key={color} />
            ))}
          </div>
          <div className={styles.indicator} style={{ left: `${position}%` }} />
        </div>
      </div>
    </ActionCard>
  );
};

export default CreditScoreGauge;
